import { FACE_VERIFICATION } from '../config';

const createAbortError = () => {
  const error = new Error('face_verification_aborted');
  error.name = 'AbortError';
  return error;
};

const parseJsonResponse = async (response) => {
  const contentType = response.headers?.get?.('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error('invalid_face_verification_response');
  }

  try {
    return await response.json();
  } catch {
    throw new Error('invalid_face_verification_response');
  }
};

export async function requestFaceVerification({
  requestId,
  imageFile,
  url = FACE_VERIFICATION.URL,
  timeoutMs = FACE_VERIFICATION.TIMEOUT_MS,
  signal,
} = {}) {
  if (!requestId) {
    throw new Error('verification_request_id_missing');
  }
  if (!(imageFile instanceof Blob)) {
    throw new Error('face_image_missing');
  }

  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort();

  if (signal?.aborted) {
    throw createAbortError();
  }
  signal?.addEventListener('abort', abortFromCaller, { once: true });

  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const formData = new FormData();
  formData.append('image', imageFile, imageFile.name || 'face.jpg');
  formData.append('requestId', requestId);

  let response;
  let data;
  try {
    response = await fetch(url, {
      method: 'POST',
      body: formData,
      credentials: 'omit',
      signal: controller.signal,
    });
    data = await parseJsonResponse(response);
  } catch (error) {
    if (timedOut) {
      throw new Error('face_verification_timeout');
    }
    if (signal?.aborted || error?.name === 'AbortError') {
      throw createAbortError();
    }
    if (error?.message === 'invalid_face_verification_response') {
      throw error;
    }
    throw new Error('face_verification_unavailable');
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abortFromCaller);
  }

  if (!response.ok) {
    const error = new Error(`face_verification_http_${response.status}`);
    error.httpStatus = response.status;
    error.reason = data?.reason;
    throw error;
  }
  if (data?.requestId !== requestId) {
    throw new Error('verification_request_id_mismatch');
  }
  if (typeof data?.isValid !== 'boolean') {
    throw new Error('invalid_face_verification_response');
  }

  return data;
}
